import { Box, Button, alpha, lighten, styled, useTheme } from "@mui/material";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { useLocation, Link as RouterLink } from "react-router-dom";
import {
  ContactLink,
  HeaderInner,
  HeaderInnerLeft,
  HeaderInnerRight,
  HeaderLink,
  NeedHelpImg,
} from "../Sidebar/styles";
import { TDSP_PORTAL_URL } from "utility/constants";

interface HeaderWrapperProps {
  collapsed: boolean;
}

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<HeaderWrapperProps>(
  ({ theme, collapsed }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 3)};
        right: 0;
        z-index: 10;
        background-color: ${alpha(
          theme.header.background ?? "white",
          1
        )};        
        position: fixed;
        justify-content: space-between;
        width: 100%;
        box-shadow: 0px 4px 22.4px rgba(0, 0, 0, 0.06);
        @media (min-width: ${theme.breakpoints.values.sm}px) {
            left: ${collapsed ? "50px" : theme.sidebar.width};
            width: auto;
        }
  `
);

interface HeaderProps {
  collapsed: boolean;
}

function Header({ collapsed }: Readonly<HeaderProps>) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const activeClass = (route: string) => pathname === route ? 'header-link-selected' : '';

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`,
      }}
      collapsed={collapsed}
    >
      <HeaderInner>
        <HeaderInnerLeft>
          <li>
            <HeaderLink
              to="/"
              className={activeClass('/')}
            >
              Home
            </HeaderLink>
          </li>
        </HeaderInnerLeft>
        <HeaderInnerRight>
          <NeedHelpImg alt="help-icon" src="/assets/help-icon.svg" /> Need
          help?&nbsp;&nbsp;{" "}
          <ContactLink href="mailto:DataSciencePlatform@toyota.com">
            Contact Us
          </ContactLink>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          {TDSP_PORTAL_URL && (
          <Button
            component={RouterLink}
            to={TDSP_PORTAL_URL}
            target="_self"
            rel="noreferrer"
            variant="contained"
            size="medium"
            startIcon={<SwapHorizRoundedIcon />}
            sx={{
              background: `linear-gradient(135deg, ${theme.colors.error.main} 0%, ${theme.colors.error.dark} 100%)`,
              color: theme.colors.alpha.trueWhite[100],
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '24px',
              px: 2,
              py: 0.4,
              boxShadow: `0 2px 8px ${alpha(theme.colors.error.main, 0.35)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.colors.error.dark} 0%, ${theme.colors.error.main} 100%)`,
                boxShadow: `0 4px 14px ${alpha(theme.colors.error.main, 0.45)}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            Switch to TDSP Portal
          </Button>
          )}
        </HeaderInnerRight>
      </HeaderInner>
    </HeaderWrapper>
  );
}

export default Header;
